import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Users, Heart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { audienceInsights } from '../data/mock';

const AudienceInsights = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState({});
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Count-up animation
  useEffect(() => {
    if (isVisible) {
      const duration = 1500;
      const steps = 60;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        const progress = currentStep / steps;

        const newCounts = {};
        audienceInsights.demographics.items.forEach(item => {
          const target = parseInt(item.value);
          newCounts[item.label] = Math.floor(target * progress);
        });
        audienceInsights.ageGender.items.forEach(item => {
          const target = parseInt(item.value);
          newCounts[item.label] = Math.floor(target * progress);
        });

        setCounts(newCounts);

        if (currentStep >= steps) {
          clearInterval(timer);
        }
      }, stepDuration);

      return () => clearInterval(timer);
    }
  }, [isVisible]);

  return (
    <section id="audience" ref={sectionRef} className="py-24 bg-gradient-to-b from-[#0B0B0B] to-[#151515]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s ease-out'
            }}
          >
            Know Your Audience
          </h2>
          <p 
            className="text-lg text-white/70 max-w-2xl mx-auto"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s ease-out 0.2s'
            }}
          >
            Highly engaged, fitness-focused followers across the USA and beyond
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Demographics Card */}
          <Card 
            className="bg-white/5 backdrop-blur-sm border-white/10 hover:bg-white/10 hover:-translate-y-2 transition-all duration-300"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s ease-out 0.3s'
            }}
          >
            <CardHeader>
              <CardTitle className="flex items-center text-white">
                <MapPin className="text-[#2563EB] mr-2" size={24} />
                {audienceInsights.demographics.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {audienceInsights.demographics.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-white/70 text-sm flex items-center">
                    <span className="mr-2">{item.flag}</span>
                    {item.label}
                  </span>
                  <span className="text-white font-semibold">
                    {isVisible ? counts[item.label] || 0 : 0}%
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Age & Gender Card */}
          <Card 
            className="bg-white/5 backdrop-blur-sm border-white/10 hover:bg-white/10 hover:-translate-y-2 transition-all duration-300"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s ease-out 0.4s'
            }}
          >
            <CardHeader>
              <CardTitle className="flex items-center text-white">
                <Users className="text-[#2563EB] mr-2" size={24} />
                {audienceInsights.ageGender.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {audienceInsights.ageGender.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-white/70 text-sm">{item.label}</span>
                  <span className="text-white font-semibold">
                    {isVisible ? counts[item.label] || 0 : 0}%
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Interests Card */}
          <Card 
            className="bg-white/5 backdrop-blur-sm border-white/10 hover:bg-white/10 hover:-translate-y-2 transition-all duration-300"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'all 0.8s ease-out 0.5s'
            }}
          >
            <CardHeader>
              <CardTitle className="flex items-center text-white">
                <Heart className="text-[#2563EB] mr-2" size={24} />
                {audienceInsights.interests.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {audienceInsights.interests.items.map((item, index) => (
                <div 
                  key={index} 
                  className="text-white/70 text-sm py-2 px-3 bg-white/5 rounded-md hover:bg-white/10 transition-colors duration-200"
                >
                  {item}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default AudienceInsights;
